import { hasSupabaseConfig, supabase } from '../lib/supabase';

export type ProcessVoteChoice = 'approve' | 'reject';

export interface ProcessRoundRecord {
  roundNo: number;
  attemptNo: number;
  leader: string;
  teamMembers: string[];
  votes: Record<string, ProcessVoteChoice>;
  isForcedRound: boolean;
  missionSuccessVotes: number;
  missionFailVotes: number;
  missionResult: 'success' | 'fail' | 'pending';
  ladyOfLakePlayer?: string;
  useExcalibur?: boolean;
  excaliburTargetPlayer?: string;
}

export interface ProcessRecordVersion {
  version: number;
  savedAt: string;
  rounds: ProcessRoundRecord[];
  ladyOfLakeChain?: string[];
  summary?: string;
}

export interface ProcessRecordDraft {
  id: string;
  title: string;
  playerCount: number;
  startPlayer: string;
  source: 'offline_quick_save' | 'manual';
  status: 'draft' | 'final';
  boundGameId?: number;
  boundGameLabel?: string;
  ladyOfLakeChain?: string[];
  createdAt: string;
  updatedAt: string;
  versions: ProcessRecordVersion[];
}

const KEY = 'avalon_process_record_drafts_v1';
const REMOTE_MARKER = '[PROCESS_DRAFT]';
let remoteHydratePromise: Promise<ProcessRecordDraft[]> | null = null;

function emitChange() {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent('avalon-process-drafts-updated'));
}

function compareDraftTitles(a: ProcessRecordDraft, b: ProcessRecordDraft): number {
  const titleA = a.title || '';
  const titleB = b.title || '';
  const titleCompare = titleA.localeCompare(titleB, 'zh-CN', { numeric: true, sensitivity: 'base' });
  if (titleCompare !== 0) return -titleCompare;
  return (b.updatedAt || '').localeCompare(a.updatedAt || '');
}

function readAll(): ProcessRecordDraft[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeAll(items: ProcessRecordDraft[]) {
  localStorage.setItem(KEY, JSON.stringify(items));
  emitChange();
  void persistRemote(items);
}

async function persistRemote(items: ProcessRecordDraft[]) {
  if (!hasSupabaseConfig) return;
  try {
    const { data: existingRows, error: queryError } = await supabase.from('game_comments').select('*');
    if (queryError) throw queryError;

    const remoteIds = (existingRows || [])
      .filter((row: any) => row.player_nickname === REMOTE_MARKER)
      .map((row: any) => row.id)
      .filter((id: any) => id != null);

    if (remoteIds.length > 0) {
      const { error: deleteError } = await supabase.from('game_comments').delete().in('id', remoteIds);
      if (deleteError) throw deleteError;
    }

    if (items.length === 0) return;

    const payloadRows = items.map((item) => ({
      game_record_id: item.boundGameId || 24,
      player_nickname: REMOTE_MARKER,
      comment_text: JSON.stringify(item),
      is_deleted: 'y',
    }));

    const { error: insertError } = await supabase.from('game_comments').insert(payloadRows as any);
    if (insertError) throw insertError;
  } catch (error) {
    console.warn('[processRecordStorage] remote sync failed', error);
  }
}

async function loadRemote(): Promise<ProcessRecordDraft[] | null> {
  if (!hasSupabaseConfig) return null;
  try {
    const { data, error } = await supabase
      .from('game_comments')
      .select('id, game_record_id, player_nickname, comment_text, is_deleted, created_at, updated_at');
    if (error) return null;
    const rows = (data || []).filter((row: any) => row.player_nickname === REMOTE_MARKER);
    const drafts = rows
      .map((row: any) => {
        try {
          const parsed = JSON.parse(row.comment_text || '{}');
          if (parsed && parsed.boundGameId != null) {
            const normalizedGameId = Number(parsed.boundGameId);
            parsed.boundGameId = Number.isFinite(normalizedGameId) ? normalizedGameId : undefined;
          }
          return parsed as ProcessRecordDraft;
        } catch {
          return null;
        }
      })
      .filter((draft): draft is ProcessRecordDraft => Boolean(draft));
    return drafts;
  } catch (error) {
    console.warn('[processRecordStorage] remote load failed', error);
    return null;
  }
}

function writeLocalOnly(items: ProcessRecordDraft[]) {
  localStorage.setItem(KEY, JSON.stringify(items));
}

function nowIso() {
  return new Date().toISOString();
}

function sameGameId(left: number | string | undefined | null, right: number | string | undefined | null): boolean {
  if (left == null || right == null) return false;
  return String(left) === String(right);
}

function normalizeStartPlayer(startPlayer: string, rounds: ProcessRoundRecord[]): string {
  const firstLeader = rounds.find((round) => round?.leader)?.leader?.trim();
  return firstLeader || startPlayer || '1号';
}

function normalizeLadyOfLakeValue(value?: string): string {
  return String(value || '').trim();
}

function getLadyOfLakeChainSlot(roundNo: number): number | null {
  if (!Number.isFinite(roundNo) || roundNo <= 0) return null;
  if (roundNo <= 2) return 0;
  if (roundNo <= 5) return roundNo - 2;
  return null;
}

function deriveLadyOfLakeChain(rounds: ProcessRoundRecord[]): string[] {
  const chain = ['', '', '', ''];
  for (const round of rounds) {
    const slot = getLadyOfLakeChainSlot(Number(round?.roundNo || 0));
    if (slot == null) continue;
    const value = normalizeLadyOfLakeValue(round?.ladyOfLakePlayer);
    if (!value) continue;
    if (slot === 0) {
      chain[0] = chain[0] || value;
    } else {
      chain[slot] = value;
    }
  }
  return chain;
}

function applyLadyOfLakeChain(rounds: ProcessRoundRecord[], chain?: string[]): ProcessRoundRecord[] {
  if (!Array.isArray(chain) || chain.length === 0) return rounds;
  return rounds.map((round) => {
    const slot = getLadyOfLakeChainSlot(Number(round?.roundNo || 0));
    if (slot == null) return round;
    const value = normalizeLadyOfLakeValue(round?.ladyOfLakePlayer);
    if (value) return round;
    const nextValue = normalizeLadyOfLakeValue(chain[slot]);
    if (!nextValue) return round;
    return { ...round, ladyOfLakePlayer: nextValue };
  });
}

function applyLadyOfLakeChainToRounds(rounds: ProcessRoundRecord[], chain?: string[]): ProcessRoundRecord[] {
  const normalizedRounds = applyLadyOfLakeChain(rounds, chain);
  return normalizedRounds;
}

export const processRecordStorage = {
  list(): ProcessRecordDraft[] {
    return readAll().sort(compareDraftTitles);
  },

  async syncFromRemote(): Promise<ProcessRecordDraft[]> {
    if (remoteHydratePromise) {
      await remoteHydratePromise;
      return this.list();
    }
    remoteHydratePromise = (async () => {
      const remoteItems = await loadRemote();
      if (remoteItems && remoteItems.length > 0) {
        writeLocalOnly(remoteItems);
      }
      return readAll();
    })();
    try {
      await remoteHydratePromise;
    } finally {
      remoteHydratePromise = null;
    }
    return this.list();
  },

  createDraft(input: {
    title: string;
    playerCount: number;
    startPlayer: string;
    source: 'offline_quick_save' | 'manual';
    rounds?: ProcessRoundRecord[];
  }): ProcessRecordDraft {
    const items = readAll();
    const createdAt = nowIso();
    const rounds = input.rounds || [];
    const ladyOfLakeChain = deriveLadyOfLakeChain(rounds);
    const draft: ProcessRecordDraft = {
      id: `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      title: input.title,
      playerCount: input.playerCount,
      startPlayer: normalizeStartPlayer(input.startPlayer, rounds),
      source: input.source,
      status: 'draft',
      ladyOfLakeChain,
      createdAt,
      updatedAt: createdAt,
      versions: [
        {
          version: 1,
          savedAt: createdAt,
          rounds: applyLadyOfLakeChainToRounds(rounds, ladyOfLakeChain),
          ladyOfLakeChain,
        },
      ],
    };
    items.unshift(draft);
    writeAll(items);
    return draft;
  },

  upsertOfflineQuickDraft(input: {
    title?: string;
    playerCount: number;
    startPlayer: string;
    rounds: ProcessRoundRecord[];
  }): ProcessRecordDraft {
    const items = readAll();
    const now = nowIso();
    const rounds = input.rounds || [];
    const ladyOfLakeChain = deriveLadyOfLakeChain(rounds);
    const startPlayer = normalizeStartPlayer(input.startPlayer, rounds);
    const existing = items.find((d) => d.source === 'offline_quick_save' && d.status === 'draft' && !d.boundGameId);
    if (!existing) {
      return this.createDraft({
        title: input.title || `线下记录 ${new Date().toLocaleString('zh-CN')}`,
        playerCount: input.playerCount,
        startPlayer,
        source: 'offline_quick_save',
        rounds,
      });
    }
    const nextVersion = (existing.versions[existing.versions.length - 1]?.version || 0) + 1;
    if (input.title) {
      existing.title = input.title;
    }
    existing.playerCount = input.playerCount;
    existing.startPlayer = startPlayer;
    existing.ladyOfLakeChain = ladyOfLakeChain;
    existing.updatedAt = now;
    existing.versions.push({
      version: nextVersion,
      savedAt: now,
      rounds: applyLadyOfLakeChainToRounds(rounds, ladyOfLakeChain),
      ladyOfLakeChain,
    });
    writeAll(items);
    return existing;
  },

  bindDraft(draftId: string, gameId: number, gameLabel: string): boolean {
    const items = readAll();
    const target = items.find((d) => d.id === draftId);
    if (!target) return false;
    const conflict = items.find((d) => d.id !== draftId && sameGameId(d.boundGameId, gameId));
    if (conflict) return false;
    target.boundGameId = gameId;
    target.boundGameLabel = gameLabel;
    target.status = 'final';
    target.updatedAt = nowIso();
    writeAll(items);
    return true;
  },

  unbindDraft(draftId: string): boolean {
    const items = readAll();
    const target = items.find((d) => d.id === draftId);
    if (!target) return false;
    target.boundGameId = undefined;
    target.boundGameLabel = undefined;
    target.status = 'draft';
    target.updatedAt = nowIso();
    writeAll(items);
    return true;
  },

  rebindDraftForGame(oldGameId: number, newGameId: number, gameLabel: string): boolean {
    const items = readAll();
    const target = items.find((draft) => sameGameId(draft.boundGameId, oldGameId));
    if (!target) return false;
    const conflict = items.find((draft) => draft.id !== target.id && sameGameId(draft.boundGameId, newGameId));
    if (conflict) return false;
    target.boundGameId = newGameId;
    target.boundGameLabel = gameLabel;
    target.status = 'final';
    target.updatedAt = nowIso();
    writeAll(items);
    return true;
  },

  getByGameId(gameId: number): ProcessRecordDraft | null {
    const items = readAll();
    return items.find((draft) => sameGameId(draft.boundGameId, gameId)) || null;
  },

  removeDraft(draftId: string): boolean {
    const items = readAll();
    const next = items.filter((d) => d.id !== draftId);
    if (next.length === items.length) return false;
    writeAll(next);
    return true;
  },

  getById(draftId: string): ProcessRecordDraft | null {
    const items = readAll();
    return items.find((d) => d.id === draftId) || null;
  },

  updateDraft(
    draftId: string,
    input: {
      title: string;
      playerCount: number;
      startPlayer: string;
      rounds: ProcessRoundRecord[];
      summary?: string;
    }
  ): boolean {
    const items = readAll();
    const target = items.find((d) => d.id === draftId);
    if (!target) return false;
    const now = nowIso();
    const nextVersion = (target.versions[target.versions.length - 1]?.version || 0) + 1;
    const rounds = input.rounds || [];
    const ladyOfLakeChain = deriveLadyOfLakeChain(rounds);
    target.title = input.title;
    target.playerCount = input.playerCount;
    target.startPlayer = normalizeStartPlayer(input.startPlayer, rounds);
    target.ladyOfLakeChain = ladyOfLakeChain;
    target.updatedAt = now;
    target.versions.push({
      version: nextVersion,
      savedAt: now,
      rounds: applyLadyOfLakeChainToRounds(rounds, ladyOfLakeChain),
      ladyOfLakeChain,
      summary: input.summary,
    });
    writeAll(items);
    return true;
  },

  upsertImportedDraft(input: {
    title: string;
    playerCount: number;
    startPlayer: string;
    source: 'offline_quick_save' | 'manual';
    status: 'draft' | 'final';
    rounds: ProcessRoundRecord[];
    boundGameId?: number;
    boundGameLabel?: string;
  }): ProcessRecordDraft {
    const items = readAll();
    const now = nowIso();
    const rounds = input.rounds || [];
    const ladyOfLakeChain = deriveLadyOfLakeChain(rounds);
    const matchIndex = input.boundGameId != null
      ? items.findIndex((d) => sameGameId(d.boundGameId, input.boundGameId))
      : items.findIndex((d) => d.title === input.title && d.source === input.source);

    const draft: ProcessRecordDraft = {
      id: matchIndex >= 0 ? items[matchIndex].id : `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      title: input.title,
      playerCount: input.playerCount,
      startPlayer: normalizeStartPlayer(input.startPlayer, rounds),
      source: input.source,
      status: input.status,
      boundGameId: input.boundGameId,
      boundGameLabel: input.boundGameLabel,
      ladyOfLakeChain,
      createdAt: matchIndex >= 0 ? items[matchIndex].createdAt : now,
      updatedAt: now,
      versions: [
        {
          version: 1,
          savedAt: now,
          rounds: applyLadyOfLakeChainToRounds(rounds, ladyOfLakeChain),
          ladyOfLakeChain,
        },
      ],
    };

    if (matchIndex >= 0) {
      items[matchIndex] = draft;
    } else {
      items.unshift(draft);
    }
    writeAll(items);
    return draft;
  },
};
